import React from 'react';

// Minimal, dependency-free Markdown renderer for the YouTube setup guide.
// Handles headings, paragraphs, ordered/unordered lists, tables, fenced code,
// blockquotes, horizontal rules, and inline code / bold / links.

function renderInline(text, keyBase) {
  const nodes = [];
  let rest = String(text ?? '');
  let i = 0;
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/;
  while (rest.length) {
    const m = rest.match(pattern);
    if (!m) { nodes.push(rest); break; }
    if (m.index > 0) nodes.push(rest.slice(0, m.index));
    const tok = m[0];
    if (tok.startsWith('`')) {
      nodes.push(<code key={`${keyBase}-${i}`} className="bg-slate-800 px-1 rounded text-emerald-300 text-[11px] break-all">{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={`${keyBase}-${i}`} className="text-slate-100">{tok.slice(2, -2)}</strong>);
    } else {
      const lm = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      nodes.push(<a key={`${keyBase}-${i}`} href={lm[2]} target="_blank" rel="noreferrer" className="text-sky-400 underline break-all">{lm[1]}</a>);
    }
    rest = rest.slice(m.index + tok.length);
    i++;
  }
  return nodes;
}

const splitCells = (s) => s.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

export default function MarkdownLite({ text }) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim().startsWith('```')) {
      const buf = []; i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) { buf.push(lines[i]); i++; }
      i++; blocks.push({ t: 'pre', v: buf.join('\n') }); continue;
    }
    if (/^\s*---\s*$/.test(line)) { blocks.push({ t: 'hr' }); i++; continue; }
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { blocks.push({ t: `h${h[1].length}`, v: h[2] }); i++; continue; }
    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
      const header = line; i += 2; const body = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) { body.push(lines[i]); i++; }
      blocks.push({ t: 'table', header, body }); continue;
    }
    if (line.trim().startsWith('>')) {
      const buf = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) { buf.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      blocks.push({ t: 'quote', v: buf.join(' ') }); continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/, '')); i++; }
      blocks.push({ t: 'ul', items }); continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, '')); i++; }
      blocks.push({ t: 'ol', items }); continue;
    }
    if (line.trim() === '') { i++; continue; }
    const buf = [line]; i++;
    while (i < lines.length && lines[i].trim() !== ''
      && !/^\s*(#{1,6}\s|[-*]\s|\d+\.\s|>|\|)/.test(lines[i])
      && !lines[i].trim().startsWith('```') && !/^\s*---\s*$/.test(lines[i])) {
      buf.push(lines[i]); i++;
    }
    blocks.push({ t: 'p', v: buf.join(' ') });
  }

  return (
    <div className="text-[12px] leading-relaxed text-slate-300 space-y-2">
      {blocks.map((b, k) => {
        if (b.t === 'hr') return <hr key={k} className="border-slate-700 my-3" />;
        if (b.t === 'pre') return <pre key={k} className="bg-slate-800 p-2 rounded overflow-x-auto text-[11px] whitespace-pre-wrap break-all">{b.v}</pre>;
        if (b.t === 'h1') return <h1 key={k} className="text-base font-bold text-slate-100 mt-2">{renderInline(b.v, k)}</h1>;
        if (b.t === 'h2') return <h2 key={k} className="text-sm font-bold text-slate-100 mt-3">{renderInline(b.v, k)}</h2>;
        if (b.t && b.t[0] === 'h') return <h3 key={k} className="text-[12px] font-semibold text-slate-200 mt-2">{renderInline(b.v, k)}</h3>;
        if (b.t === 'quote') return <blockquote key={k} className="border-l-2 border-slate-600 pl-3 text-slate-400 italic">{renderInline(b.v, k)}</blockquote>;
        if (b.t === 'ul') return <ul key={k} className="list-disc pl-5 space-y-0.5">{b.items.map((it, j) => <li key={j}>{renderInline(it, `${k}-${j}`)}</li>)}</ul>;
        if (b.t === 'ol') return <ol key={k} className="list-decimal pl-5 space-y-0.5">{b.items.map((it, j) => <li key={j}>{renderInline(it, `${k}-${j}`)}</li>)}</ol>;
        if (b.t === 'table') {
          const heads = splitCells(b.header);
          return (
            <table key={k} className="w-full text-[11px] border-collapse">
              <thead><tr>{heads.map((hd, j) => <th key={j} className="text-left border-b border-slate-700 py-1 pr-3">{renderInline(hd, `${k}-h-${j}`)}</th>)}</tr></thead>
              <tbody>{b.body.map((r, ri) => {
                const cells = splitCells(r);
                return <tr key={ri}>{cells.map((c, ci) => <td key={ci} className="border-b border-slate-800 py-1 pr-3 align-top">{renderInline(c, `${k}-${ri}-${ci}`)}</td>)}</tr>;
              })}</tbody>
            </table>
          );
        }
        return <p key={k} className="my-1">{renderInline(b.v, k)}</p>;
      })}
    </div>
  );
}
